"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { BotIcon, ChevronRightIcon, FlaskConicalIcon, RefreshCcwIcon, type LucideIcon } from "lucide-react";
import { toast } from "sonner";
import { AgentTestBatchDialog } from "@/components/agent-test-batch-dialog";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Spinner } from "@/components/ui/spinner";
import { useTeams } from "@/hooks/use-teams";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

// Admin → Evaluations (superadmin): a hub of test suites. Each suite is a card;
// opening one shows its own page. Today the only suite is Agents: on-demand
// batches and starter tests (docs/agent-lifecycle.md). Batches are queued here
// and run in the background; watching and cancelling runs, schedules and the
// kill switch are on the Automations page. Starter tests are written in this browser
// with the superadmin's own token, for agents whose tests they can manage.
// There is no shared runner account. Add a future suite (e.g. tool tests) as
// another entry in TEST_SUITES with its own view. Every action is enforced in
// the database (superadmin checks in the RPCs); the sidebar's role gate is
// presentation only.

/** Counts across every agent, not just the ones the superadmin can manage. */
type StarterStatus = {
  without_tests: number;
  completed: number;
  tests_written: number;
  per_agent: number;
};

/** An agent the signed-in superadmin can write starter tests for. */
type StarterAgent = { agent_id: string; name: string };

type StarterProgress = {
  status: "waiting" | "drafting" | "saved" | "skipped" | "failed";
  count?: number;
  error?: string;
};

/** Agents drafted at once: enough to move along, few enough not to swamp the model. */
const STARTER_CONCURRENCY = 2;

const PILL = "rounded-full px-2 py-0.5 text-xs font-medium";

type AgentTestsViewProps = { onBack: () => void };

/** The Agents suite: on-demand batches and starter tests. */
function AgentTestsView({ onBack }: AgentTestsViewProps) {
  const { teams } = useTeams();
  const [loading, setLoading] = useState(true);
  const [batchOpen, setBatchOpen] = useState(false);
  const [starter, setStarter] = useState<StarterStatus | null>(null);
  const [starterConfirmOpen, setStarterConfirmOpen] = useState(false);
  const [myStarterAgents, setMyStarterAgents] = useState<StarterAgent[]>([]);
  const [writingStarter, setWritingStarter] = useState(false);
  const [starterProgress, setStarterProgress] = useState<Record<string, StarterProgress>>({});
  const [starterRunAgents, setStarterRunAgents] = useState<StarterAgent[]>([]);
  const stopStarterRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [starterRes, myAgentsRes] = await Promise.all([
      supabase.rpc("get_agent_starter_tests_status"),
      supabase.rpc("list_my_starter_test_agents"),
    ]);
    if (starterRes.error) console.error("[AgentTestsPanel] starter tests status failed:", starterRes.error);
    if (myAgentsRes.error) console.error("[AgentTestsPanel] starter test agents failed:", myAgentsRes.error);
    setStarter((starterRes.data ?? null) as StarterStatus | null);
    setMyStarterAgents((myAgentsRes.data ?? []) as StarterAgent[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void load();
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Writes starter tests agent by agent from this browser with the signed-in
  // superadmin's token: /api/agent-tests/starter drafts from the agent's own
  // setup and saves through save_my_starter_tests (which re-checks they can
  // manage the agent and notifies the owner). Closing the page stops it.
  const handleWriteStarter = async () => {
    setStarterConfirmOpen(false);
    const agents = myStarterAgents;
    if (agents.length === 0) return;
    stopStarterRef.current = false;
    setWritingStarter(true);
    setStarterRunAgents(agents);
    setStarterProgress(Object.fromEntries(agents.map((a) => [a.agent_id, { status: "waiting" } as StarterProgress])));
    const update = (agentId: string, progress: StarterProgress) =>
      setStarterProgress((prev) => ({ ...prev, [agentId]: progress }));

    const writeOne = async (agent: StarterAgent) => {
      update(agent.agent_id, { status: "drafting" });
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error("Please sign in.");
        const res = await fetch("/api/agent-tests/starter", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ agentId: agent.agent_id }),
        });
        const body = (await res.json().catch(() => null)) as { data?: { saved: number }; error?: string } | null;
        if (!res.ok) throw new Error(body?.error || "Couldn't draft tests.");
        const saved = body?.data?.saved ?? 0;
        update(agent.agent_id, saved > 0 ? { status: "saved", count: saved } : { status: "skipped" });
      } catch (error) {
        console.error("[AgentTestsPanel] starter tests failed:", { agentId: agent.agent_id, error });
        update(agent.agent_id, { status: "failed", error: error instanceof Error ? error.message : String(error) });
      }
    };

    let next = 0;
    const worker = async () => {
      while (next < agents.length && !stopStarterRef.current) {
        await writeOne(agents[next++]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(STARTER_CONCURRENCY, agents.length) }, worker));

    setWritingStarter(false);
    toast.success(stopStarterRef.current ? "Starter tests stopped." : "Starter tests finished.");
    await load();
  };

  const starterCounts = useMemo(() => {
    const values = Object.values(starterProgress);
    return {
      saved: values.filter((p) => p.status === "saved").length,
      failed: values.filter((p) => p.status === "failed").length,
      done: values.filter((p) => p.status !== "waiting" && p.status !== "drafting").length,
    };
  }, [starterProgress]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Agent tests"
        description="Run agent tests on demand and write starter tests."
        onBack={onBack}
        showBack
        actions={
          <div className="ml-auto flex items-center gap-1.5">
            <Button type="button" variant="outline" size="sm" className="gap-1.5" disabled={loading} onClick={() => void load()}>
              <RefreshCcwIcon className="size-3.5" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button type="button" size="sm" className="gap-1.5" onClick={() => setBatchOpen(true)}>
              <FlaskConicalIcon className="size-3.5" />
              <span className="hidden sm:inline">Run agent tests now</span>
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <div className="mx-auto w-full max-w-5xl space-y-6">
          <p className="text-sm text-muted-foreground">
            Watch and cancel runs, and manage schedules, on the{" "}
            <Link href="/automations?tab=runs" className="underline underline-offset-2 hover:text-foreground">
              Automations page
            </Link>
            .
          </p>
          {/* ── Starter tests ──────────────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Starter tests</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  Drafts {starter?.per_agent ?? 3} tests for every agent you can manage that has none, from its own
                  instructions and sources, and saves them under your name. Owners are notified to review them. Keep
                  this page open while it runs.
                </p>
              </div>
              {writingStarter ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  onClick={() => {
                    stopStarterRef.current = true;
                  }}
                >
                  <Spinner className="size-3.5" /> Stop
                </Button>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={loading || myStarterAgents.length === 0}
                  onClick={() => setStarterConfirmOpen(true)}
                >
                  Write starter tests
                </Button>
              )}
            </CardHeader>
            <CardContent className="text-sm">
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Spinner className="size-4" /> Checking…
                </div>
              ) : !starter ? (
                <p className="text-muted-foreground">Couldn&apos;t load starter test status.</p>
              ) : (
                <div className="space-y-2">
                  <p className="tabular-nums text-foreground">
                    {starter.without_tests} agent{starter.without_tests === 1 ? "" : "s"} without tests ·{" "}
                    {myStarterAgents.length} you can manage
                  </p>
                  {starter.completed > 0 ? (
                    <span className={cn(PILL, "inline-block bg-activated/15 tabular-nums text-activated")}>
                      {starter.completed} done · {starter.tests_written} tests
                    </span>
                  ) : null}
                  {starterRunAgents.length > 0 ? (
                    <div className="space-y-2 pt-2">
                      <p className="text-xs tabular-nums text-muted-foreground">
                        {starterCounts.done} of {starterRunAgents.length} done · {starterCounts.saved} saved
                        {starterCounts.failed > 0 ? ` · ${starterCounts.failed} failed` : ""}
                      </p>
                      <ul className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-border bg-card px-3 py-2">
                        {starterRunAgents.map((agent) => {
                          const progress = starterProgress[agent.agent_id] ?? { status: "waiting" };
                          return (
                            <li key={agent.agent_id} className="flex items-center justify-between gap-2 py-0.5">
                              <span className="min-w-0 truncate">{agent.name}</span>
                              <span
                                title={progress.error}
                                className={cn(
                                  PILL,
                                  "shrink-0",
                                  progress.status === "saved" && "bg-activated/15 text-activated",
                                  progress.status === "failed" && "bg-destructive/15 text-destructive",
                                  (progress.status === "waiting" || progress.status === "skipped") &&
                                    "bg-muted text-muted-foreground",
                                  progress.status === "drafting" && "bg-muted text-foreground",
                                )}
                              >
                                {progress.status === "saved"
                                  ? `${progress.count} tests`
                                  : progress.status === "drafting"
                                    ? "Drafting…"
                                    : progress.status === "skipped"
                                      ? "Already has tests"
                                      : progress.status === "failed"
                                        ? "Failed"
                                        : "Waiting"}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      <AgentTestBatchDialog
        open={batchOpen}
        onOpenChange={(open) => {
          setBatchOpen(open);
          if (!open) void load();
        }}
        teams={teams.filter((team) => !team.is_home).map((team) => ({ id: team.id, name: team.name }))}
      />

      <ConfirmDialog
        open={starterConfirmOpen}
        onOpenChange={setStarterConfirmOpen}
        title="Write starter tests"
        description={`Drafts ${starter?.per_agent ?? 3} tests for each of the ${myStarterAgents.length} agents you can manage that have none, from each agent's own instructions and sources. They're saved under your name and owners are notified to review them. Agents that get tests in the meantime are skipped. Keep this page open until it finishes.`}
        confirmLabel="Write starter tests"
        variant="default"
        onConfirm={() => void handleWriteStarter()}
      />

    </div>
  );
}

// ── Hub ──────────────────────────────────────────────────────────────────────

type SuiteId = "agents";

const TEST_SUITES: { id: SuiteId; title: string; description: string; Icon: LucideIcon }[] = [
  {
    id: "agents",
    title: "Agents",
    description: "Run agent tests now and write starter tests.",
    Icon: BotIcon,
  },
];

type EvaluationsPanelProps = { onBack?: () => void };

export function EvaluationsPanel({ onBack }: EvaluationsPanelProps) {
  const [suite, setSuite] = useState<SuiteId | null>(null);

  if (suite === "agents") return <AgentTestsView onBack={() => setSuite(null)} />;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader title="Evaluations" description="Automated scoring across Woods AI." onBack={onBack} />
      <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TEST_SUITES.map(({ id, title, description, Icon }) => {
            return (
              <button
                key={id}
                type="button"
                onClick={() => setSuite(id)}
                className="group flex flex-col gap-4 rounded-xl border border-border bg-card p-5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                    <Icon className="size-5" />
                  </div>
                </div>
                <div className="min-w-0 space-y-1">
                  <p className="flex items-center gap-1 text-sm font-medium text-foreground">
                    {title}
                    <ChevronRightIcon className="size-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
